// Chip tool MCP nella chat PA: compatto e delicato, con icona contestuale
// (web/MCP) + nome del tool + badge durata; al click si espande mostrando
// argomenti, risultato e fonti. Identico live e da storico: i metadati ricchi
// (args/durata/risultato/fonti) sono persistiti col messaggio e rimappati al
// reload, quindi il chip non degrada più a header spoglio.
import { useState } from 'react';
import { ChevronRight, Globe, Wrench, X, Loader2, BookOpen } from 'lucide-react';
import { isWebSearchTool } from '../paToolDisplay';
import type { PaToolBadge, PaRagMeta } from '../paStreamReducer';

export function ToolCallCard({ tool }: { tool: PaToolBadge }) {
  const [open, setOpen] = useState(false);
  const web = isWebSearchTool(tool.name);
  const running = tool.status === 'running';
  const error = tool.status === 'error';
  const hasArgs = !!tool.args && Object.keys(tool.args).length > 0;
  const hasResult = !!tool.resultPreview;
  const citations = tool.citations ?? [];
  const hasCitations = citations.length > 0;
  const hasDetails = hasArgs || hasResult || hasCitations;

  // Icona contestuale + colore neutro (solo running/errore deviano dal grigio).
  const Icon = running ? Loader2 : error ? X : web ? Globe : Wrench;
  const iconClass = running
    ? 'animate-spin text-amber-500'
    : error
      ? 'text-red-500'
      : 'text-muted-foreground';

  return (
    <div className="flex max-w-full flex-col items-start">
      <button
        type="button"
        disabled={!hasDetails}
        onClick={() => hasDetails && setOpen(o => !o)}
        className={`group inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/80 py-[3px] pl-2 pr-2.5 transition-colors ${
          hasDetails ? 'cursor-pointer hover:border-border hover:bg-muted' : 'cursor-default'
        }`}
      >
        <Icon className={`h-3 w-3 shrink-0 ${iconClass}`} />
        <span className="truncate max-w-[180px] font-mono text-[10.5px] leading-none text-foreground">{tool.name}</span>
        {typeof tool.durationMs === 'number' && (
          <span className="shrink-0 rounded-full bg-muted/70 px-1.5 py-[1px] text-[8.5px] font-semibold leading-none tabular-nums text-muted-foreground">
            {tool.durationMs}ms
          </span>
        )}
        {hasDetails && (
          <ChevronRight className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        )}
      </button>
      {hasDetails && open && (
        <div className="mt-1 w-full max-w-[480px] animate-in fade-in-0 slide-in-from-top-1 space-y-2 rounded-lg border border-border bg-card px-2.5 py-2 text-[11px]">
          {hasArgs && (
            <div>
              <div className="mb-1 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">Argomenti</div>
              <pre className="overflow-x-auto rounded bg-muted p-2 text-[10.5px] leading-snug text-muted-foreground">
                {JSON.stringify(tool.args, null, 2)}
              </pre>
            </div>
          )}
          {hasResult && (
            <div>
              <div className="mb-1 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">Risultato</div>
              <pre className="max-h-60 overflow-auto rounded bg-muted p-2 text-[10.5px] leading-snug text-muted-foreground whitespace-pre-wrap break-words">
                {tool.resultPreview}
              </pre>
            </div>
          )}
          {hasCitations && (
            <div>
              <div className="mb-1 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">Fonti</div>
              <ul className="space-y-1">
                {citations.map((c, i) => (
                  <li key={i}>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-start gap-1 text-[10.5px] text-blue-600 hover:underline break-all"
                    >
                      <Globe className="mt-[1px] h-3 w-3 shrink-0 text-muted-foreground" />
                      <span>{c.title?.trim() || c.url}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function KnowledgeBaseRow({ rag }: { rag: PaRagMeta }) {
  // Mostrata solo quando la KB ha effettivamente restituito chunk: "0 chunk
  // recuperati" è rumore (la ricerca non ha prodotto contesto utile). Stesso
  // linguaggio visivo del chip tool (pill compatto, icona contestuale KB).
  if (!rag || rag.chunks <= 0) return null;
  return (
    <div className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/80 py-[3px] pl-2 pr-2.5 text-[10.5px] text-muted-foreground">
      <BookOpen className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="leading-none">
        Knowledge base: <span className="font-semibold text-foreground">{rag.chunks}</span> chunk
      </span>
      <span className="shrink-0 rounded-full bg-muted/70 px-1.5 py-[1px] text-[8.5px] font-semibold leading-none tabular-nums text-muted-foreground">
        {rag.latencyMs}ms
      </span>
    </div>
  );
}
